import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Select, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface TableFooterProps {
  currentPage: number;
  totalRecords: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (value: number) => void;
  visibleRecordsCount?: number;
}

const FooterContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2), // 12px 16px
  gap: theme.spacing(2),
  height: "60px",
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const SummaryText = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.pxToRem(15),
  lineHeight: 1.2,
  color: theme.palette.text.secondary, // rgba(27, 27, 27, 0.7) ~ text.secondary
}));

const RightSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5), // 4px
}));

const PageChip = styled(Box)<{ $active?: boolean }>(({ theme, $active }) => ({
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 24,
  cursor: "pointer",
  backgroundColor: $active
    ? theme.palette.primary.light // Например, светлый оттенок primary
    : "transparent",
}));

const PageNumber = styled(Typography)<{ $active?: boolean }>(({ theme, $active }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.pxToRem(15),
  lineHeight: 1.2,
  color: $active ? theme.palette.primary.contrastText : theme.palette.text.primary,
}));

const RowsSelectContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const RowsLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.pxToRem(15),
  lineHeight: 1.2,
  color: theme.palette.text.secondary,
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  "&.MuiInputBase-root": {
    borderRadius: 24,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(15),
    minHeight: 36,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(3), // под иконку
  },
  "& .MuiSelect-select": {
    paddingRight: theme.spacing(3),
  },
  "& .MuiSelect-icon": {
    right: theme.spacing(1),
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
}));

const TableFooter: React.FC<TableFooterProps> = ({
  currentPage,
  totalRecords,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  visibleRecordsCount,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));

  const displayedCount = visibleRecordsCount ?? rowsPerPage;
  const summaryText = t("tableFooter.records", {
    count: displayedCount,
    total: totalRecords,
  });

  // Простой массив всех страниц (осторожнее, если страниц много)
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <FooterContainer>
      <SummaryText>{summaryText}</SummaryText>

      <RightSection>
        {/* Пагинация */}
        <PaginationContainer>
          {pageNumbers.map((p) => {
            const active = p === currentPage;
            return (
              <PageChip key={p} $active={active} onClick={() => onPageChange(p)}>
                <PageNumber $active={active}>{p}</PageNumber>
              </PageChip>
            );
          })}
        </PaginationContainer>

        {/* Выбор количества строк */}
        <RowsSelectContainer>
          <RowsLabel>{t("tableFooter.showRows", "Показать строк:")}</RowsLabel>
          <StyledSelect
            variant="standard"
            disableUnderline
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          >
            {[5, 10, 20, 50, 100].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </StyledSelect>
        </RowsSelectContainer>
      </RightSection>
    </FooterContainer>
  );
};

export default TableFooter;
